import { useState, useEffect, useCallback, useRef } from "react";
import { adminService } from "@b2b/services";
import { useAuth } from "../auth/Authprovider";
import { PendingUser, UserFilters } from "../types/user.types";

interface UseUserListOptions {
  activeTab?: string;
  searchTerm?: string;
  advancedFilters?: UserFilters;
  autoFetch?: boolean;
}

const getUserDisplayStatus = (user: PendingUser): "pending" | "approved" | "rejected" => {
  const role = user.role;

  if (role === "SELLER" || role === "BUYER_SELLER") {
    if (user.sellerStatus === "APPROVED") return "approved";
    if (user.sellerStatus === "REJECTED") return "rejected";
    return "pending";
  }

  if (user.isActive) return "approved";
  if (user.company?.approvalStatus === "rejected") return "rejected";
  return "pending";
};

export function useUserList(options: UseUserListOptions = {}) {
  const { isLoading: authLoading, isAuthenticated } = useAuth();

  const activeTab = options.activeTab || "all";
  const searchTerm = options.searchTerm || "";
  const normalizedSearchTerm = searchTerm
    .trim()
    .replace(/^["']+|["']+$/g, "")
    .replace(/\s+/g, " ");
  // Use ref for advancedFilters to prevent unnecessary re-renders
  const advancedFiltersRef = useRef(options.advancedFilters || {});
  // Only update ref if filters actually changed (deep comparison via JSON)
  const filtersKey = JSON.stringify(options.advancedFilters || {});
  const prevFiltersKeyRef = useRef(filtersKey);

  useEffect(() => {
    if (prevFiltersKeyRef.current !== filtersKey) {
      advancedFiltersRef.current = options.advancedFilters || {};
      prevFiltersKeyRef.current = filtersKey;
    }
  }, [filtersKey, options.advancedFilters]);
  const autoFetch = options.autoFetch !== false;

  const [users, setUsers] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeRequestIdRef = useRef(0);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Use ref to track current pagination to avoid dependency issues
  const paginationRef = useRef(pagination);
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const fetchUsers = useCallback(
    async (page?: number, forceRefresh = false) => {
      const targetPage = page ?? paginationRef.current.page;
      const requestId = ++activeRequestIdRef.current;
      try {
        setLoading(true);
        setError(null);

        const queryParams: any = {
          page: targetPage,
          limit: paginationRef.current.limit,
        };

        // Add search
        if (normalizedSearchTerm) {
          queryParams.search = normalizedSearchTerm;
        }

        // Add tab-based filter
        if (activeTab === "pending") {
          queryParams.approvalStatus = "pending";
        } else if (activeTab === "approved") {
          queryParams.isActive = true;
        } else if (activeTab === "rejected") {
          queryParams.approvalStatus = "rejected";
        }

        // Add advanced filters
        Object.entries(advancedFiltersRef.current).forEach(([key, value]) => {
          if (
            value !== undefined &&
            value !== "" &&
            key !== "page" &&
            key !== "limit"
          ) {
            queryParams[key] = value;
          }
        });

        // For SUPER_ADMIN in Approved tab, rely on role filter only.
        // Backend excludes admin roles when status filters are present.
        if (activeTab === "approved" && queryParams.role === "SUPER_ADMIN") {
          delete queryParams.isActive;
          delete queryParams.approvalStatus;
        }

        // SUPER_ADMIN users don't have a pending/rejected approval workflow.
        // Prevent backend status query from returning buyer/seller rows for this filter combo.
        if (
          queryParams.role === "SUPER_ADMIN" &&
          (activeTab === "pending" || activeTab === "rejected")
        ) {
          if (requestId !== activeRequestIdRef.current) {
            return;
          }
          setUsers([]);
          setPagination((prev) => ({
            ...prev,
            page: targetPage,
            total: 0,
            totalPages: 1,
          }));
          return;
        }

        const result = await adminService.getAllUsers(queryParams);

        const parseUserListResponse = (response: any) => {
          let parsedUsers: PendingUser[] = [];
          let parsedPagination = null;

          if (Array.isArray(response)) {
            parsedUsers = response;
          } else if (response && typeof response === "object") {
            parsedUsers = Array.isArray(response.data) ? response.data : [];
            if (response.pagination) {
              parsedPagination = response.pagination;
            }
          }

          return {
            users: Array.isArray(parsedUsers) ? parsedUsers : [],
            pagination: parsedPagination,
          };
        };

        let { users: usersData, pagination: paginationData } =
          parseUserListResponse(result);

        // Fallback: if spaced search returns no rows, retry once with compact term.
        // This supports names entered with spaces while preserving existing behavior.
        if (
          usersData.length === 0 &&
          normalizedSearchTerm.includes(" ")
        ) {
          const compactSearch = normalizedSearchTerm.replace(/\s+/g, "");
          if (compactSearch) {
            const fallbackResult = await adminService.getAllUsers({
              ...queryParams,
              search: compactSearch,
            });
            const parsedFallback = parseUserListResponse(fallbackResult);
            usersData = parsedFallback.users;
            paginationData = parsedFallback.pagination;
          }
        }

        // Final fallback for spaced full-name queries:
        // fetch users with existing non-search filters and apply local name matching.
        if (usersData.length === 0 && normalizedSearchTerm.includes(" ")) {
          const normalizeForCompare = (value?: string | null) =>
            (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

          const searchTokens = normalizedSearchTerm
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean)
            .map((token) => normalizeForCompare(token))
            .filter(Boolean);

          const compactSearch = normalizeForCompare(normalizedSearchTerm);
          const paramsWithoutSearch = { ...queryParams };
          delete paramsWithoutSearch.search;

          const firstScanResult = await adminService.getAllUsers({
            ...paramsWithoutSearch,
            page: 1,
            limit: 100,
          });
          const parsedFirstScan = parseUserListResponse(firstScanResult);
          const totalScanPages = Math.min(
            parsedFirstScan.pagination?.totalPages || 1,
            50
          );

          const allCandidates: PendingUser[] = [...parsedFirstScan.users];
          for (let currentPage = 2; currentPage <= totalScanPages; currentPage++) {
            const nextPageResult = await adminService.getAllUsers({
              ...paramsWithoutSearch,
              page: currentPage,
              limit: 100,
            });
            const parsedNextPage = parseUserListResponse(nextPageResult);
            if (parsedNextPage.users.length === 0) break;
            allCandidates.push(...parsedNextPage.users);
          }

          const matchedUsers = allCandidates.filter((user) => {
            const fullName = normalizeForCompare(
              `${user.firstName || ""} ${user.lastName || ""}`
            );
            const firstName = normalizeForCompare(user.firstName);
            const lastName = normalizeForCompare(user.lastName);
            const email = normalizeForCompare(user.email);
            const company = normalizeForCompare(user.company?.name);

            if (compactSearch && fullName.includes(compactSearch)) return true;

            if (searchTokens.length === 0) return false;

            return searchTokens.every((token) => {
              if (!token) return true;
              return (
                fullName.includes(token) ||
                firstName.includes(token) ||
                lastName.includes(token) ||
                email.includes(token) ||
                company.includes(token)
              );
            });
          });

          const start = (targetPage - 1) * paginationRef.current.limit;
          const end = start + paginationRef.current.limit;
          usersData = matchedUsers.slice(start, end);
          paginationData = {
            page: targetPage,
            limit: paginationRef.current.limit,
            total: matchedUsers.length,
            totalPages: Math.max(
              1,
              Math.ceil(matchedUsers.length / paginationRef.current.limit)
            ),
          };
        }

        // Keep tab content consistent with status badge when a specific role is selected.
        // This avoids mixed statuses appearing in Pending/Approved/Rejected tabs.
        const selectedRole = advancedFiltersRef.current?.role;
        const isSpecificRoleSelected = !!selectedRole && selectedRole !== "all";
        const isStatusTab = activeTab === "pending" || activeTab === "rejected";

        if (isSpecificRoleSelected && isStatusTab) {
          const strictTabUsers = usersData.filter(
            (user) => getUserDisplayStatus(user) === activeTab
          );
          usersData = strictTabUsers;

          if (paginationData) {
            paginationData = {
              ...paginationData,
              total: strictTabUsers.length,
              totalPages: 1,
            };
          }
        }

        // Ignore stale responses from older requests when filters/tabs changed quickly.
        if (requestId !== activeRequestIdRef.current) {
          return;
        }

        setUsers(Array.isArray(usersData) ? usersData : []);

        if (paginationData) {
          // Only update pagination if it actually changed to avoid unnecessary re-renders
          setPagination((prev) => {
            if (
              prev.page === paginationData.page &&
              prev.limit === paginationData.limit &&
              prev.total === paginationData.total &&
              prev.totalPages === paginationData.totalPages
            ) {
              return prev; // No change, return same object
            }
            // Update prevPageRef to match the new pagination
            prevPageRef.current = paginationData.page;
            return {
              page: paginationData.page,
              limit: paginationData.limit,
              total: paginationData.total,
              totalPages: paginationData.totalPages,
            };
          });
        }
      } catch (err) {
        if (requestId !== activeRequestIdRef.current) {
          return;
        }
        console.error("Error fetching users:", err);
        setError("Failed to fetch users. Please try again.");
      } finally {
        if (requestId === activeRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [activeTab, normalizedSearchTerm]
  );

  const refresh = useCallback(async () => {
    await fetchUsers(paginationRef.current.page, true);
  }, [fetchUsers]);

  // Track previous page to detect pagination changes
  const prevPageRef = useRef(pagination.page);
  const isInitialMountRef = useRef(true);
  const isFilterChangeRef = useRef(false);
  const isPaginationChangeRef = useRef(false);

  // Reset pagination when filters change
  useEffect(() => {
    isFilterChangeRef.current = true;
    isPaginationChangeRef.current = false;
    setPagination((prev) => ({ ...prev, page: 1 }));
    prevPageRef.current = 1; // Update ref to match reset
  }, [activeTab, normalizedSearchTerm, filtersKey]);

  // Watch for pagination page changes and trigger fetch
  // Note: This is a fallback - onPageChange in UserList.tsx also calls fetchUsers directly
  useEffect(() => {
    // Skip on initial mount (handled by auto-fetch effect)
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevPageRef.current = pagination.page;
      return;
    }

    // Skip if this is a filter-initiated reset (handled by auto-fetch effect)
    if (isFilterChangeRef.current) {
      prevPageRef.current = pagination.page;
      return;
    }

    // Skip if this is already a pagination change (onPageChange already called fetchUsers)
    if (isPaginationChangeRef.current) {
      prevPageRef.current = pagination.page;
      return;
    }

    // Skip if loading (already fetching)
    if (loading) {
      return;
    }

    // Only fetch if page actually changed and autoFetch is enabled
    if (prevPageRef.current !== pagination.page && autoFetch) {
      if (authLoading || !isAuthenticated) return;

      const targetPage = pagination.page;

      // Update ref immediately to prevent duplicate calls
      prevPageRef.current = targetPage;

      // Fetch users for the new page
      fetchUsers(targetPage, false);
    }
  }, [
    pagination.page,
    autoFetch,
    authLoading,
    isAuthenticated,
    fetchUsers,
    loading,
  ]);

  // Auto-fetch when enabled (for filter changes)
  useEffect(() => {
    if (!autoFetch) return;
    if (authLoading || !isAuthenticated) return;

    let isMounted = true;
    let timeoutId: NodeJS.Timeout;
    let retryTimeoutId: NodeJS.Timeout;

    const loadData = async () => {
      const { getAccessToken } = await import("@b2b/services");
      const token = await getAccessToken();
      if (!token) {
        if (isMounted) {
          retryTimeoutId = setTimeout(() => loadData(), 500);
        }
        return;
      }

      if (!isMounted) return;

      // Debounce search requests
      timeoutId = setTimeout(
        async () => {
          if (isMounted) {
            await fetchUsers(1, false);
            // Update refs after fetch completes
            prevPageRef.current = 1;
            isInitialMountRef.current = false;
            // Clear filter change flag after fetch completes
            isFilterChangeRef.current = false;
          }
        },
        normalizedSearchTerm ? 500 : 0
      );
    };

    loadData();

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
    };
  }, [
    activeTab,
    normalizedSearchTerm,
    filtersKey,
    autoFetch,
    authLoading,
    isAuthenticated,
    fetchUsers,
  ]);

  return {
    users,
    loading,
    error,
    pagination,
    setPagination,
    refresh,
    fetchUsers,
  };
}
